import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import './tracking_copying_text.scss';

const copyrightInfringementMessage =
  'Copying articles to share with others is a breach ' +
  'of our terms and conditions and copywriter policy. Please use the sharing options. ' +
  'Devex Pro members may share up to 10 articles per month using the Pro share tool.';

const TrackingCopyingText = ({ querySelector, minCopyLength }) => {
  const [showAlert, setShowAlert] = useState(false);

  /**
   * Function triggered as event listener on the `copy` event. If user copied more content
   * than the minCopyLength (defined as binding), add some copyright infringement to copied
   * content. When the user will paste his clipboard, he will then see an added copyright
   * infringement message.
   * This solution is inspired by https://stackoverflow.com/a/42091750 and aims to support
   * most browsers. Note that, in Firefox, if user tries to copy content using Right-click
   * then `Copy`, the action won't succeed, resulting in console warning. That means that user
   * won't be able to copy big portion of content from Pro article this way.
   *
   * @returns {undefined}
   */
  const appendCopyrightMessageToClipboard = () => {
    const text = window
      .getSelection()
      .toString()
      .replace(/[\n\r]+/g, '');
    let textarea = document.createElement('textarea');
    textarea.textContent = `${copyrightInfringementMessage} ${text}`;
    textarea.style.position = 'fixed';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      return document.execCommand('cut');
    } catch (e) {
      return false;
    } finally {
      document.body.removeChild(textarea);
    }
  };

  /**
   * Callback verifies if length of copied content is higher than `minCopyLength` binding,
   * if that's the case, show copy text warning message.
   * Modifies user's clipboard to prepend copyright infringement message. Once the user
   * clicks on 'X' button in the alert, alert will be hided.
   *
   * @returns {undefined}
   */
  const copyEventListenerCallback = () => {
    const copiedText = window.getSelection().toString();
    if (copiedText.length > minCopyLength) {
      appendCopyrightMessageToClipboard();
      setShowAlert(true);
    }
  };

  /**
   * On initialization, if querySelector element exists, it adds an event listener on copy
   * which triggers copyEventListenerCallback
   */
  useEffect(() => {
    const contentBody = document.querySelector(querySelector);
    if (contentBody) {
      contentBody.addEventListener('copy', copyEventListenerCallback);
    }
  }, [querySelector]);

  return (
    <div data-testid='tracking-copying-text'>
      {showAlert && (
        <div className='alert alert-warning copy-text-warning z-depth-1' role='alert'>
          <div className='container'>
            <strong>
              Copying articles to share with others is a breach of our{' '}
              <a
                className='link-blue-orange'
                target='_blank'
                href='https://support.devex.com/hc/en-us/articles/203927810-Devex-User-Agreement'
              >
                terms and conditions
              </a>{' '}
              and{' '}
              <a
                className='link-blue-orange'
                target='_blank'
                href='https://support.devex.com/hc/en-us/articles/203927810-Devex-User-Agreement#personal-profiles-section'
              >
                copywriter policy
              </a>
            </strong>
            . Please use the sharing options on the left side of the article. Devex Pro members
            may share up to 10 articles per month using the Pro share tool (
            <i className='fal icon-share-alt ms-1 me-1'></i>).
            <button className='close a' onClick={() => setShowAlert(false)}>
              <i className='fal icon-times'></i>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

TrackingCopyingText.propTypes = {
  querySelector: PropTypes.string.isRequired,
  minCopyLength: PropTypes.number.isRequired,
};

export default TrackingCopyingText;
